/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import api from './api';
import type { LoginCredentials, LoginResponse, User } from '../types';

export interface InviteDetails {
  email: string;
  role: string;
  name: string;
  company?: string;
  phone?: string;
}

function normalizeUserRole(user: User | undefined | null): User {
  if (!user || typeof user !== 'object') {
    throw new Error('Authentication API returned an invalid response. Check admin API routing.');
  }

  const normalized = {
    ...user,
    inviteStatus: user.inviteStatus ?? 'none',
  };

  return (normalized.role as string) === 'demo' ? { ...normalized, role: 'demo_user' } : normalized;
}

export const authService = {
  async register(input: {
    email: string;
    name: string;
    password: string;
    company?: string;
    phone?: string;
  }): Promise<{ user: User; verificationEmailSent: boolean }> {
    const res = await api.post<{ user: User; verificationEmailSent: boolean }>('/api/auth/register', input);
    return {
      ...res.data,
      user: normalizeUserRole(res.data.user),
    };
  },

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const res = await api.post<LoginResponse>('/api/auth/login', credentials);
    return {
      ...res.data,
      user: normalizeUserRole(res.data.user),
    };
  },

  async getCurrentUser(): Promise<User> {
    const res = await api.get<User>('/api/auth/me');
    return normalizeUserRole(res.data);
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } finally {
      localStorage.removeItem('admin_token');
    }
  },

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/api/auth/forgot-password', { email });
  },

  async updateMyProfile(input: { name?: string; company?: string; phone?: string }): Promise<User> {
    const res = await api.patch<User>('/api/auth/me/profile', input);
    return res.data;
  },

  async resendMyVerification(): Promise<void> {
    await api.post('/api/auth/me/resend-verification');
  },

  async changeMyPassword(input: { currentPassword: string; newPassword: string }): Promise<void> {
    await api.post('/api/auth/me/change-password', input);
  },

  async getInviteDetails(token: string): Promise<InviteDetails> {
    const res = await api.get<InviteDetails>(`/api/auth/invite/${encodeURIComponent(token)}/details`);
    return res.data;
  },

  async acceptInvite(
    token: string,
    input: { password: string; name?: string; company?: string; phone?: string },
  ): Promise<{ success: boolean; user: User }> {
    const res = await api.post<{ success: boolean; user: User }>(
      `/api/auth/invite/${encodeURIComponent(token)}/accept`,
      input,
    );
    return {
      ...res.data,
      user: normalizeUserRole(res.data.user),
    };
  },
};
